import {Button, Col, Container, FloatingLabel, Form, Modal, Row} from "react-bootstrap";
import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {
  deleteCoursePage, getCourseDocumentList, getCoursePageList, getCourseVideoList, upsertCoursePage
} from "../api/CourseApi";
import {HtmlEditor} from "../shared/HtmlEditor";
import {toolTipRenderer} from "../utils/gridUtils";
import {fieldHasErrors, getErrorMessageForField} from "../utils/formUtils";
import {deepCopy, getConfLabel, showSuccessToast, titleCase} from "../utils/usacmUtils";
import {CONF_LABEL_KEY_COURSE, CONF_LABEL_KEY_COURSE_PAGE} from "../constants";

// A new (unsaved) page.  The order is set by the server - a new page goes on the end of the course.
const COURSE_PAGE_TEMPLATE = {
  id: null,
  title: '',
  html: '',
  page_order: null,
};

/**
 * The pages which make up one course.  The content (html) lives on the page and not on the course.
 * Every page belongs to this one course - pages are not shared between courses.
 * canEdit comes from the course - a course admin can edit any course, an instructor only their own.
 */
export function CoursePages({course, canEdit, onClose}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [pageList, setPageList] = useState([]);
  // The course documents and videos - {doc:key} in a page's html links to a document, {video:key} shows a video
  const [documentList, setDocumentList] = useState([]);
  const [videoList, setVideoList] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [deletingPage, setDeletingPage] = useState(null);
  // Edit dialog fields
  const [title, setTitle] = useState('');
  const [pageOrder, setPageOrder] = useState('');
  const [errors, setErrors] = useState([]);
  const [pageErrors, setPageErrors] = useState([]);
  const htmlEditorRef = useRef(null);
  const gridRef = useRef();
  const hasFewRows = (pageList || []).length < 12;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const pageLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE));
  const actionColWidth = canEdit ? 150 : 90;

  function loadPages() {
    if (!course?.id) {
      return;
    }
    getCoursePageList(course.id, (code, data, newErrors) => {
      if (code === 200) {
        setPageList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    loadPages();
  }, [course?.id]);

  function startEditingPage(coursePage) {
    const page = coursePage || deepCopy(COURSE_PAGE_TEMPLATE);
    setTitle(page.title || '');
    setPageOrder(page.page_order === null || page.page_order === undefined ? '' : page.page_order);
    setPageErrors([]);
    setEditingPage(page);
    loadDocumentsAndVideos();
  }

  // Loaded each time a page is opened, so documents and videos just added on the Docs / Videos tabs show up
  function loadDocumentsAndVideos() {
    getCourseDocumentList(course.id, (code, data) => {
      setDocumentList(code === 200 ? data : []);
    });
    getCourseVideoList(course.id, (code, data) => {
      setVideoList(code === 200 ? data : []);
    });
  }

  // Puts a {doc:key} / {video:key} variable into the page content where the cursor is
  function insertVariable(variable) {
    if (canEdit && htmlEditorRef.current) {
      htmlEditorRef.current.insertContent(variable);
      htmlEditorRef.current.focus();
    }
  }

  function renderVariableList(label, items, variableType, keyField) {
    if (items.length === 0) {
      return null;
    }
    return (
      <span>
        {label} : {items.map((item, index) => {
          const variable = `{${variableType}:${item[keyField]}}`;
          return (
            <span key={item.id}>
              {index > 0 && ', '}
              <code role={canEdit ? 'button' : undefined}
                    title={canEdit ? `${item.title} - click to insert` : item.title}
                    onClick={() => insertVariable(variable)}>{variable}</code>
            </span>
          );
        })}
      </span>
    );
  }

  function callUpsertPage() {
    const html = htmlEditorRef.current ? htmlEditorRef.current.getContent() : '';
    upsertCoursePage(editingPage?.id, course?.id, title, html, pageOrder,
      (code, data, newErrors) => {
        if (code === 200) {
          showSuccessToast(`${editingPage?.id ? 'Updated' : 'Added'} ${pageLabel.toLowerCase()}.`);
          setEditingPage(null);
          setPageErrors([]);
          loadPages();
        } else {
          setPageErrors(newErrors);
        }
      });
  }

  function callDeletePage() {
    deleteCoursePage(deletingPage?.id, (code, data, newErrors) => {
      setDeletingPage(null);
      if (code === 200) {
        showSuccessToast(`Deleted ${pageLabel.toLowerCase()}.`);
        setErrors([]);
        loadPages();
      } else {
        setErrors(newErrors);
      }
    });
  }

  // Column setup
  const colDefs = [
    {field: 'page_order', cellRenderer: toolTipRenderer, headerName: 'Order', maxWidth: 90, minWidth: 90},
    {
      headerName: 'Title', flex: 4, minWidth: 150, cellRenderer: toolTipRenderer,
      headerTooltip: `The title shown in the ${courseLabel.toLowerCase()} outline`,
      field: 'title',
    },
    {
      headerName: 'Action',
      sortable: false, filter: false,
      maxWidth: actionColWidth, minWidth: actionColWidth,
      cellRenderer: params => {
        return <Fragment>
          <Button type="button" onClick={() => startEditingPage(params.data)} size="sm" className="me-2">
            {canEdit ? 'Edit' : 'View'}
          </Button>
          {canEdit &&
            <Button type="button" variant="danger" onClick={() => setDeletingPage(params.data)} size="sm">
              Delete
            </Button>
          }
        </Fragment>;
      }
    },
  ];

  const defaultColDefs = {
    sortable: true,
    filter: false,
    resizable: true,
  }

  // Pages hang off a saved course, there is nothing to attach them to until it has been created
  if (!course?.id) {
    return (
      <Container fluid className="usacm-container-wide">
        <Row>
          <Col className="text-center">
            Create the {courseLabel.toLowerCase()} before adding {pageLabel.toLowerCase()}s to it.
          </Col>
        </Row>
        <Row>
          <Col className="usacm-button-row mt-3">
            <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="usacm-container-wide">

      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={pageList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row mt-3">
          <Button variant="secondary" onClick={() => onClose()}>Course List</Button>
          {canEdit &&
            <Button type="button" onClick={() => startEditingPage(null)}>Add {pageLabel}</Button>
          }
        </Col>
      </Row>

      <Modal show={!!editingPage}
             onHide={() => setEditingPage(null)}
             data-bs-focus="false"
             enforceFocus={false}  // This will prevent bootstrap from messing with the focus and breaking tinyMCE
             size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{editingPage?.id ? 'Edit' : 'Add'} {pageLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Row className="mb-3">
            <Col className="col-8">
              <Form.Group controlId="page_title">
                <FloatingLabel controlId="page_title" label="Title">
                  <Form.Control type="text"
                                placeholder="Title"
                                name="title"
                                value={title}
                                disabled={!canEdit}
                                onChange={e => setTitle(e.target.value)}
                                isInvalid={fieldHasErrors(pageErrors, 'title')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(pageErrors, 'title')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="col-4">
              <Form.Group controlId="page_order">
                <FloatingLabel controlId="page_order" label={`Order in this ${courseLabel}`}>
                  <Form.Control type="number"
                                step="1"
                                placeholder={`Order in this ${courseLabel}`}
                                name="page_order"
                                value={pageOrder}
                                disabled={!canEdit}
                                onChange={e => setPageOrder(e.target.value)}
                                isInvalid={fieldHasErrors(pageErrors, 'page_order')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(pageErrors, 'page_order')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <div className="d-flex flex-wrap align-items-baseline column-gap-3">
                <div>Content</div>
                <small className="text-muted">
                  {documentList.length === 0 && videoList.length === 0 ?
                    <span>Add documents and videos on the Docs and Videos tabs to use them here.</span> :
                    <span className="d-flex flex-wrap column-gap-3">
                      {renderVariableList('Documents', documentList, 'doc', 'doc_key')}
                      {renderVariableList('Videos', videoList, 'video', 'video_key')}
                    </span>
                  }
                </small>
              </div>
              <div style={fieldHasErrors(pageErrors, 'html') ? {border: '1px solid red'} : {}}>
                <HtmlEditor
                  key={editingPage?.id || 'new'} // remount so the editor picks up the page being edited
                  initialValue={editingPage?.html || ''}
                  onInit={(evt, editor) => htmlEditorRef.current = editor}
                  disabled={!canEdit}
                />
              </div>
              {fieldHasErrors(pageErrors, 'html') &&
                <div className="text-center mb-3 usacm-error-message">
                  {getErrorMessageForField(pageErrors, 'html')}
                </div>
              }
            </Col>
          </Row>

          {getErrorMessageForField(pageErrors, '') &&
            <Row>
              <Col className="text-center usacm-error-message">
                {getErrorMessageForField(pageErrors, '')}
              </Col>
            </Row>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingPage(null)}>Cancel</Button>
          {canEdit &&
            <Button variant="primary" onClick={() => callUpsertPage()}>
              {editingPage?.id ? 'Update' : 'Add'} {pageLabel}
            </Button>
          }
        </Modal.Footer>
      </Modal>

      <Modal show={!!deletingPage}
             onHide={() => setDeletingPage(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{deletingPage?.title}"?
          This deletes the {pageLabel.toLowerCase()} and its content for good.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeletingPage(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => callDeletePage()}>Delete {pageLabel}</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
